import React, { useMemo } from "react";

import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import LabeledInfo from "Components/LabeledInfo";
import { METADATA_TYPES } from "Constants/constants";
import { Button, CloseButton } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import {
  deploymentSizingApiEnabledSelector,
  nonCalculatedOneDeploymentResourceSelector
} from "Reducers/environment/deployment";
import { gitProjectSelector } from "Reducers/project/project";
import {
  AppInfos,
  RouteInfos,
  ServiceInfos,
  WorkerInfos
} from "src/organization/common/components";
import { ServiceTreeEventPayload } from "src/organization/common/containers/ServicesDisplay/ServicesTree/ServiceGraph/ServiceTree";

import * as S from "./styles";

type DetailDialogProps = {
  kind?: string;
  metadata?: ServiceTreeEventPayload["metadata"];
  onClick: (value?: Record<string, unknown>) => void;
  onClose: () => void;
  transform?: string;
  workers?: number;
};

const DetailDialog = ({
  metadata,
  onClick,
  onClose,
  transform
}: DetailDialogProps) => {
  const intl = useIntl();
  const location = useLocation();

  const { projectId, organizationId, environmentId } = useDecodedParams<{
    projectId: string;
    organizationId: string;
    environmentId: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const envId = environmentId || project?.default_branch;

  const isSizingApiEnabled = useSelectorWithUrlParams(
    deploymentSizingApiEnabledSelector,
    { environmentId: envId }
  );

  const [InfoComponent, resourceType] = useMemo(() => {
    let InfoComponent;
    let resourceType;
    switch (metadata?.kind) {
      case METADATA_TYPES.app:
        InfoComponent = AppInfos;
        resourceType = "webapps";
        break;
      case METADATA_TYPES.service:
        InfoComponent = ServiceInfos;
        resourceType = "services";
        break;
      case METADATA_TYPES.worker:
        InfoComponent = WorkerInfos;
        resourceType = "workers";
        break;
      default:
        InfoComponent = RouteInfos;
    }

    return [InfoComponent, resourceType];
  }, [metadata]);

  const currentNonCalculatedResource = useSelectorWithUrlParams(
    nonCalculatedOneDeploymentResourceSelector,
    {
      environmentId: envId,
      resourceType,
      appName: metadata?.appName
    }
  );

  const version = metadata?.type?.split(":")[1];

  return (
    <S.Wrapper transform={transform}>
      <S.TitleBar>
        <LabeledInfo
          value={metadata?.name || "Router"}
          label={version ? `Version ${version}` : ""}
        />
        <CloseButton onClick={onClose} />
      </S.TitleBar>
      <S.Grid>
        <InfoComponent
          metadata={metadata}
          isSizingApiEnabled={isSizingApiEnabled}
          {...(resourceType && currentNonCalculatedResource)}
        />
      </S.Grid>
      <S.LinkWrapper>
        <Button
          tabIndex={0}
          variant="link"
          onClick={() => onClick(metadata)}
          analyticId="view_details"
        >
          {intl.formatMessage({ id: "view_details" })}
        </Button>
        {isSizingApiEnabled && metadata?.name && (
          <Button
            tabIndex={0}
            variant="link"
            analyticId="configure"
            to={getUrl({
              key: "organization.project.environment.configureResources",
              props: {
                organizationId,
                projectId,
                environmentId: envId || "",
                location: location.pathname + location.search,
                queryParams: ""
              }
            })}
          >
            {intl.formatMessage({ id: "configure" })}
          </Button>
        )}
      </S.LinkWrapper>
    </S.Wrapper>
  );
};

export default DetailDialog;
